import { useInfiniteQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { queryKeys } from '@/api/query-keys'
import {
  FULL_PAGE_NOTIFICATION_SIZE,
  mapNotificationRow,
  type BellNotificationItem,
  type BellNotificationListMeta,
} from './notifications-queries'
import { SP_NOTIFICATIONS_API_BASE } from '../lib/notifications-api-paths'
import {
  isDevNotificationBackendDisabled,
  isNotificationUnavailableError,
  markDevNotificationBackendUnavailable,
  notificationDevQueryOptions,
} from '../lib/notificationDevBackend'

type NotificationsPageResult = {
  items: BellNotificationItem[]
  page: number
  meta: BellNotificationListMeta
  hasMore: boolean
}

export function useNotificationsInfinite(isRead: boolean) {
  return useInfiniteQuery({
    queryKey: queryKeys.spNotifications.infinite(isRead, FULL_PAGE_NOTIFICATION_SIZE),
    initialPageParam: 1,
    enabled: notificationDevQueryOptions.enabled() && !isDevNotificationBackendDisabled(),
    throwOnError: false,
    meta: { suppressGlobalError: true },
    retry: (failureCount, error) => {
      if (isNotificationUnavailableError(error)) {
        markDevNotificationBackendUnavailable(error)
        return false
      }
      return import.meta.env.DEV ? false : failureCount < 2
    },
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.get<
        ApiResponse<Array<Record<string, unknown>>>
      >(
        SP_NOTIFICATIONS_API_BASE,
        {
          params: {
            page: pageParam,
            pageSize: FULL_PAGE_NOTIFICATION_SIZE,
            isRead: String(isRead),
          },
        },
      )
      const rawMeta = data.meta as Record<string, unknown> | undefined
      const page = typeof rawMeta?.page === 'number' ? rawMeta.page : pageParam
      const pageSize =
        typeof rawMeta?.per_page === 'number' ? rawMeta.per_page : FULL_PAGE_NOTIFICATION_SIZE
      const total = typeof rawMeta?.total === 'number' ? rawMeta.total : data.data.length
      const items = data.data
        .map((row) => mapNotificationRow(row))
        .filter((row): row is BellNotificationItem => row !== null)
      const hasMore = page * pageSize < total
      return {
        items,
        page,
        meta: {
          total,
          page,
          per_page: pageSize,
          total_pages: typeof rawMeta?.total_pages === 'number' ? rawMeta.total_pages : 0,
          unreadCount: typeof rawMeta?.unreadCount === 'number' ? rawMeta.unreadCount : 0,
          readCount: typeof rawMeta?.readCount === 'number' ? rawMeta.readCount : 0,
        },
        hasMore,
      } satisfies NotificationsPageResult
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  })
}
